/**
 * chatIntentBuffer — what someone asked of the chat before its engine loaded,
 * held and delivered once it has (PR-S, 2026-09-22).
 *
 * The chat engine now arrives a moment after the page paints. Two requests
 * can reach it in that gap, and today both would be LOST with nobody told:
 *
 *   - `globalchat:open` — a tool tile on /justiceisurright (or a landing CTA)
 *     asking the chat to open, optionally with a phrase. Dispatchers fire and
 *     forget; with no listener the click does nothing.
 *   - the service worker's `OPEN_THREAD` message — a notification tap that
 *     found this window. It carries no acknowledgement, so an unheard message
 *     is simply gone.
 *
 * Both are held (the LATEST of each — a second tap supersedes the first) and
 * delivered exactly once, after the engine's own listeners exist. The other
 * requests that reach the chat already have a fallback of their own
 * (cancelable handshakes whose callers navigate when nobody claims them), so
 * they are deliberately left alone.
 *
 * Leaf only — it must never import the engine (scripts/check-chat-first-paint.mjs).
 */

import { requestOpenThreadUrl } from '../../lib/threadDeepLink';

/** The engine listens for this on `document` (useGlobalChat.ts). */
export const GLOBAL_CHAT_OPEN_EVENT = 'globalchat:open';

interface BufferState {
  installed: boolean;
  drained: boolean;
  /** Wrapped, so an event whose detail is `undefined` still counts as held. */
  open: { detail: unknown } | null;
  threadUrl: string | null;
  teardown: (() => void) | null;
}

const fresh = (): BufferState => ({
  installed: false, drained: false, open: null, threadUrl: null, teardown: null,
});

let state: BufferState = fresh();

function serviceWorkerContainer(): ServiceWorkerContainer | null {
  try {
    return typeof navigator !== 'undefined' && 'serviceWorker' in navigator ? navigator.serviceWorker : null;
  } catch {
    return null;
  }
}

/** Start holding. Idempotent; a no-op once the buffer has been drained. */
export function installChatIntentBuffer(): void {
  if (state.installed || state.drained || typeof document === 'undefined') return;
  const onOpen = (e: Event) => {
    state.open = { detail: (e as CustomEvent).detail };
  };
  const onServiceWorkerMessage = (e: MessageEvent) => {
    const data = e.data as { type?: string; url?: unknown } | null;
    if (data && data.type === 'OPEN_THREAD' && typeof data.url === 'string') state.threadUrl = data.url;
  };
  document.addEventListener(GLOBAL_CHAT_OPEN_EVENT, onOpen);
  const container = serviceWorkerContainer();
  try { container?.addEventListener('message', onServiceWorkerMessage); } catch { /* no container */ }
  state.teardown = () => {
    document.removeEventListener(GLOBAL_CHAT_OPEN_EVENT, onOpen);
    try { container?.removeEventListener('message', onServiceWorkerMessage); } catch { /* noop */ }
  };
  state.installed = true;
}

function stopHolding(): Pick<BufferState, 'open' | 'threadUrl'> {
  state.teardown?.();
  const held = { open: state.open, threadUrl: state.threadUrl };
  state = { ...fresh(), drained: true };
  return held;
}

/** A thread link nobody in this window claimed opens the way a link does. */
function openThreadUrl(url: string): void {
  if (!requestOpenThreadUrl(url)) window.location.assign(url);
}

/**
 * The engine is mounted and listening: stop holding FIRST (so the replay is
 * not captured by the buffer itself), then deliver each held request once.
 */
export function drainChatIntentBuffer(): { deliveredOpen: boolean; deliveredThreadUrl: string | null } {
  const held = stopHolding();
  if (held.open) {
    document.dispatchEvent(new CustomEvent(GLOBAL_CHAT_OPEN_EVENT, { detail: held.open.detail }));
  }
  if (held.threadUrl) openThreadUrl(held.threadUrl);
  return { deliveredOpen: Boolean(held.open), deliveredThreadUrl: held.threadUrl };
}

/**
 * The engine will not load (its download failed for good). A held thread link
 * still opens — as a page load, which is what a link does with no chat to
 * claim it. A held "open the chat" has nothing to open and is dropped, loudly.
 */
export function releaseChatIntentBufferWithoutChat(): void {
  const held = stopHolding();
  if (held.open) console.warn('[chat] a request to open the chat arrived, but the chat could not be loaded');
  if (held.threadUrl) window.location.assign(held.threadUrl);
}

/** Test seam only. */
export function __resetChatIntentBufferForTests(): void {
  state.teardown?.();
  state = fresh();
}
